import { z } from "zod";

import { formatCycle } from "@/modules/dev/utils/formatCycle";
import { getCurrentCycle } from "@/modules/dev/utils/getCurrentCycle";

import { DEV_SCENARIOS, getScenarioCycles } from "./scenarios";

export const ALL_CYCLES = "all";

export type DevCycleFilter = number | typeof ALL_CYCLES;

/** Search params of the `/dev` home: `?cycle=22` or `?cycle=all`. */
export const devIndexSearchSchema = z.object({
  cycle: z
    .union([z.literal(ALL_CYCLES), z.coerce.number().int().min(0)])
    .optional()
    .catch(undefined),
});

// Cycle chips beyond the current and previous ones, so the row stays one line.
const MAX_EXTRA_CYCLES = 2;

/**
 * The home's filter when the URL names none: the latest cycle up to the
 * current one that has a scenario, or all cycles when none has.
 */
export const getDefaultCycleFilter = (
  current = getCurrentCycle(),
): DevCycleFilter => {
  const cycles = DEV_SCENARIOS.map((scenario) => scenario.cycle).filter(
    (cycle) => cycle <= current,
  );

  return cycles.length > 0 ? Math.max(...cycles) : ALL_CYCLES;
};

/**
 * The filter's chips, newest first: the current and previous cycles, up to
 * two more cycles that have scenarios, the selected cycle, then All.
 */
export const getCycleFilterOptions = (
  selected: DevCycleFilter,
  current = getCurrentCycle(),
) => {
  const extras = getScenarioCycles()
    .filter((cycle) => cycle !== current && cycle !== current - 1)
    .slice(0, MAX_EXTRA_CYCLES);
  const cycles = new Set([current, current - 1, ...extras]);
  if (selected !== ALL_CYCLES) cycles.add(selected);

  return [
    ...[...cycles]
      .filter((cycle) => cycle >= 0)
      .sort((a, b) => b - a)
      .map((cycle) => ({ value: String(cycle), label: formatCycle(cycle) })),
    { value: ALL_CYCLES, label: "All" },
  ];
};
